/**
 * Редактор документа зворотної тари (Порція C — повний editor).
 * Параметризований за kind: 'transfer' (передача застави) | 'return' (повернення).
 * За зразком ServiceActEditor: toolbar (зберегти/провести/розпровести/видалити),
 * багаторядкові items з авторозрахунком суми застави.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import {
  tareTransfers, tareReturns, type TareItem,
  type TransferDirection, type ReturnDirection,
} from '@/api/tare';
import DatePopover from '@/components/DatePopover';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

type Kind = 'transfer' | 'return';

const DIRS: Record<Kind, { v: string; label: string }[]> = {
  transfer: [
    { v: 'TO_BUYER', label: 'Покупцю (видали заставу)' },
    { v: 'FROM_SUPPLIER', label: 'Від постачальника (отримали)' },
  ],
  return: [
    { v: 'FROM_BUYER', label: 'Від покупця (повернув тару)' },
    { v: 'TO_SUPPLIER', label: 'Постачальнику (повернули)' },
  ],
};

interface Row extends TareItem { }

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  direction: string;
  comment: string;
  items: Row[];
}

const newRow = (n: number): Row => ({
  line_number: n, nomenclature_id: '', quantity: '1', deposit_price: '0', amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function extractError(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: { msg?: string }) => d.msg ?? '').join('; ');
  return 'Помилка';
}

export default function TareEditor({ kind }: { kind: Kind }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const api = kind === 'transfer' ? tareTransfers : tareReturns;
  const basePath = kind === 'transfer' ? '/tare-transfers' : '/tare-returns';
  const title = kind === 'transfer' ? 'Передача тари' : 'Повернення тари';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('documents.tare.manage');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll(),
  });

  const docQuery = useQuery({
    queryKey: ['tare-doc', kind, id],
    queryFn: () => api.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>({
    organization_id: '', document_number: '',
    document_date: new Date().toISOString().slice(0, 16),
    contractor_id: '', direction: DIRS[kind][0]!.v, comment: '', items: [],
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data as {
        organization_id: string; document_number: string; document_date: string;
        contractor_id: string; direction: string; comment: string | null;
        items: { line_number: number; nomenclature_id: string; quantity: string; deposit_price: string; amount: string }[];
      };
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        direction: d.direction,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number, nomenclature_id: it.nomenclature_id,
          quantity: String(it.quantity), deposit_price: String(it.deposit_price),
          amount: String(it.amount),
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && ((docQuery.data as { posted?: boolean })?.posted ?? false);
  const total = form.items.reduce((s, e) => s + Number(e.amount || 0), 0);

  function setItem(i: number, patch: Partial<Row>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        if ('quantity' in patch || 'deposit_price' in patch) {
          next.amount = (Number(next.quantity || 0) * Number(next.deposit_price || 0)).toFixed(2);
        }
        return next;
      }),
    }));
  }
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  const removeRow = (i: number) => setForm((f) => ({
    ...f, items: f.items.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, line_number: idx + 1 })),
  }));

  const buildPayload = () => ({
    organization_id: form.organization_id,
    contractor_id: form.contractor_id,
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    direction: form.direction as TransferDirection & ReturnDirection,
    comment: form.comment || null,
    items: form.items.filter((it) => it.nomenclature_id),
  });

  const create = useMutation({
    mutationFn: () => api.create(buildPayload() as never),
    onSuccess: (doc: { id: string }) => {
      qc.invalidateQueries({ queryKey: ['tare', kind] });
      navigate(`${basePath}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.update(id!, {
      contractor_id: form.contractor_id,
      document_date: new Date(form.document_date).toISOString(),
      direction: form.direction as TransferDirection & ReturnDirection,
      comment: form.comment || null,
      items: form.items.filter((it) => it.nomenclature_id),
    } as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tare-doc', kind, id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: create → post одним кліком.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await api.create(buildPayload() as never) as { id: string };
      try {
        await api.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`${basePath}/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tare', kind] });
      navigate(`${basePath}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: update → post одним кліком.
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await api.update(id!, {
        contractor_id: form.contractor_id,
        document_date: new Date(form.document_date).toISOString(),
        direction: form.direction as TransferDirection & ReturnDirection,
        comment: form.comment || null,
        items: form.items.filter((it) => it.nomenclature_id),
      } as never);
      await api.post(id!);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tare-doc', kind, id] }); qc.invalidateQueries({ queryKey: ['tare', kind] }); },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => api.unpost(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tare-doc', kind, id] }); qc.invalidateQueries({ queryKey: ['tare', kind] }); },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tare', kind] }); navigate(basePath); },
  });

  const canSave = !!form.organization_id && !!form.contractor_id
    && form.items.some((it) => it.nomenclature_id && Number(it.amount) > 0);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? `Новий документ: ${title}` : `${title}: ${form.document_number}`}
          {isPosted && <span style={{ marginLeft: '0.75rem', color: 'var(--badge-green-fg)', fontSize: '1rem' }}>✓ проведено</span>}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate(basePath)}>Назад</button>
          {!isPosted && (
            <button className="primary" disabled={!canSave || create.isPending || update.isPending}
              onClick={() => (isNew ? create.mutate() : update.mutate())}>💾 Зберегти</button>
          )}
          {isNew && (
            <button className="primary" disabled={!canSave || create.isPending || createAndPost.isPending}
              onClick={() => createAndPost.mutate()} title="Зберігає та проводить документ">▶ Провести</button>
          )}
          {!isNew && !isPosted && form.items.length > 0 && (
            <button className="primary" onClick={() => updateAndPost.mutate()}
              disabled={updateAndPost.isPending || update.isPending}
              title="Зберігає поточні зміни та проводить документ">▶ Провести</button>
          )}
          {!isNew && isPosted && (
            <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>◀ Розпровести</button>
          )}
          {!isNew && !isPosted && (
            <button className="danger"
              onClick={() => confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); })}>Видалити</button>
          )}
        </div>
      </div>

      {error && <div style={{ color: 'var(--danger, #b91c1c)', marginBottom: '0.5rem' }}>{error}</div>}

      <div className="doc-grid" style={{ marginBottom: '0.75rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація</label>
          <select value={form.organization_id} disabled={!isNew || isPosted}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа {isNew && <small style={{ color: 'var(--muted)' }}>(auto)</small>}</label>
          <input value={form.document_number} disabled={isPosted}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата</label>
          <DatePopover withTime value={form.document_date} disabled={isPosted}
            onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Контрагент</label>
          <select value={form.contractor_id} disabled={isPosted}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
            <option value="">—</option>
            {(contractorsQ.data?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Напрям</label>
          <select value={form.direction} disabled={isPosted}
            onChange={(e) => setForm({ ...form, direction: e.target.value })}>
            {DIRS[kind].map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Коментар</label>
          <input value={form.comment} disabled={isPosted}
            onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
      </div>

      <TchTable>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
            <th style={{ width: 40 }}>№</th>
            <th>Тара (номенклатура)</th>
            <th style={{ width: 110 }}>Кількість</th>
            <th style={{ width: 130 }}>Заставна ціна</th>
            <th style={{ width: 130 }}>Сума</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border-soft)' }}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={isPosted}
                  onChange={(v) => setItem(i, { nomenclature_id: v })}
                />
              </td>
              <td>
                <input type="number" value={it.quantity} disabled={isPosted} style={{ width: '100%' }}
                  onChange={(e) => setItem(i, { quantity: e.target.value })} />
              </td>
              <td>
                <input type="number" value={it.deposit_price} disabled={isPosted} style={{ width: '100%' }}
                  onChange={(e) => setItem(i, { deposit_price: e.target.value })} />
              </td>
              <td style={{ textAlign: 'right' }}>{fmt(Number(it.amount || 0))}</td>
              <td>
                {!isPosted && form.items.length > 1 && (
                  <button className="danger" title="Видалити рядок" onClick={() => removeRow(i)}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>Разом застави:</td>
            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>

      {!isPosted && (
        <button type="button" style={{ marginTop: '0.5rem' }} onClick={addRow}>+ Додати рядок</button>
      )}

      <p style={{ marginTop: '0.75rem', fontSize: '0.85em', color: 'var(--muted)' }}>
        Заставна тара не оподатковується ПДВ, доки повертається (п.189.2 ПКУ).
        Проводки (стандартні рахунки; правило договору може задати інший): {kind === 'transfer'
          ? (form.direction === 'TO_BUYER' ? 'Дт 3771 — Кт 284' : 'Дт 284 — Кт 6851')
          : (form.direction === 'FROM_BUYER' ? 'Дт 284 — Кт 3771' : 'Дт 6851 — Кт 284')}.
      </p>
    </div>
  );
}
